// ---------------------------------------------------------------------------
// ButtonGroup Renderer (editor key: ButtonGroup)
// ---------------------------------------------------------------------------
// Renders a horizontal or stacked cluster of CTA links. Editor input is a
// pipe-delimited textarea ("Label | URL | variant" per line). Variants are
// constrained to a safelist so CSS owns the styling; hrefs run through the
// URL safelist so `javascript:` collapses to `#`. Published markup is a
// pure `<nav>` with `<a>` children — no JS required, fully keyboard
// reachable, screen-reader friendly.

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text, url } from "../escape.js";

interface ButtonGroupProps {
  buttons?: string;
  alignment?: "left" | "center" | "right";
  orientation?: "horizontal" | "vertical";
  ariaLabel?: string;
}

interface ButtonEntry {
  label: string;
  href: string;
  variant: string;
}

const ALLOWED_VARIANTS = new Set(["primary", "secondary", "outline"]);
const ALLOWED_ALIGNMENTS = new Set(["left", "center", "right"]);
const ALLOWED_ORIENTATIONS = new Set(["horizontal", "vertical"]);

function parseButtons(value: unknown): ButtonEntry[] {
  if (typeof value !== "string") return [];
  return value
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => {
      const [labelPart, hrefPart, variantPart] = line.split("|");
      const variant = (variantPart ?? "").trim();
      return {
        label: (labelPart ?? "").trim(),
        href: (hrefPart ?? "").trim() || "#",
        variant: ALLOWED_VARIANTS.has(variant) ? variant : "primary",
      };
    })
    .filter((entry) => entry.label.length > 0);
}

function normalizeAlignment(value: unknown): string {
  if (typeof value === "string" && ALLOWED_ALIGNMENTS.has(value)) return value;
  return "left";
}

function normalizeOrientation(value: unknown): string {
  if (typeof value === "string" && ALLOWED_ORIENTATIONS.has(value)) {
    return value;
  }
  return "horizontal";
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as ButtonGroupProps;

  const buttons = parseButtons(p.buttons);
  if (buttons.length === 0) {
    return "<!-- button-group missing buttons -->";
  }

  const alignment = normalizeAlignment(p.alignment);
  const orientation = normalizeOrientation(p.orientation);
  const ariaLabel =
    typeof p.ariaLabel === "string" && p.ariaLabel.trim()
      ? p.ariaLabel.trim()
      : "Actions";

  const links = buttons
    .map(
      (entry) =>
        `    <a class="ease-button-group__button ease-button-group__button--${attr(entry.variant)}" href="${url(entry.href)}">${text(entry.label)}</a>`,
    )
    .join("\n");

  return `<nav class="ease-button-group ease-button-group--${attr(orientation)} ease-button-group--align-${attr(alignment)}" aria-label="${attr(ariaLabel)}" data-ease-orientation="${attr(orientation)}" data-ease-alignment="${attr(alignment)}">
${links}
</nav>`;
}

registerRenderer("button-group", { render });
